import { Icon } from '../components/Icon';
import { AFTER_YOU_APPLY } from '../content/site';

/**
 * "What happens after you apply" — an onboarding stepper that removes the #1
 * reason B2B prospects don't apply: uncertainty about what they're committing
 * to. Onboarding-CRO applied ethically: step 1 is tiny and risk-free (lower
 * activation energy), each step sets an honest expectation (a concrete review
 * SLA), and the done-for-you build + "stop any time before signing" line
 * reverse the perceived risk right before the apply CTA.
 *
 * Layout: mobile-first vertical stepper (a single column with a connector rail
 * down the left), upgrading to a horizontal row on desktop. Reuses the repo's
 * utility classes (.eyebrow / .section / .btn-warm / .btn-secondary / bg-aurora
 * / shadow-card) and the inline SVG Icon set — no emoji. Decorative motion is
 * gated behind motion-safe so prefers-reduced-motion is respected.
 */
export function AfterYouApply() {
  const { eyebrow, headline, subcopy, reassurance, steps, cta, secondaryCta } = AFTER_YOU_APPLY;

  return (
    <section id="after-you-apply" className="section bg-surface-alt">
      <div className="container-content">
        <div className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="mt-4 font-heading text-3xl font-bold text-ink-900 sm:text-4xl">
            {headline}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ink-500">{subcopy}</p>
        </div>

        {/* Stepper: vertical on mobile, horizontal row on desktop. */}
        <ol
          className="mx-auto mt-12 grid max-w-5xl gap-0 lg:mt-16 lg:grid-cols-6 lg:gap-4"
          aria-label="What happens after you apply, step by step"
        >
          {steps.map((item, i) => {
            const isLast = i === steps.length - 1;
            return (
              <li
                key={item.step}
                className="relative flex gap-5 pb-8 last:pb-0 lg:flex-col lg:gap-0 lg:pb-0"
              >
                {/* Connector rail — vertical between dots on mobile, horizontal on desktop. */}
                {!isLast && (
                  <span
                    aria-hidden
                    className="absolute left-[1.375rem] top-12 bottom-0 w-px bg-line lg:left-12 lg:right-0 lg:top-[1.375rem] lg:bottom-auto lg:h-px lg:w-auto"
                  />
                )}

                {/* Numbered node */}
                <span className="relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-full bg-aurora font-heading text-base font-bold text-white shadow-card lg:h-12 lg:w-12">
                  {item.step}
                </span>

                {/* Step body */}
                <div className="min-w-0 flex-1 lg:mt-5">
                  <span className="inline-flex h-11 items-center gap-2 text-primary-700">
                    <Icon name={item.icon} className="h-5 w-5" aria-hidden />
                    <span className="font-heading text-base font-semibold text-ink-900">
                      {item.title}
                    </span>
                  </span>
                  <p className="mt-1 text-sm leading-relaxed text-ink-700">{item.description}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-700 shadow-card">
                    <Icon name="check" className="h-3.5 w-3.5" aria-hidden />
                    {item.marker}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Risk-reversal + apply CTA — placed here to convert the moment the path feels safe. */}
        <div className="mx-auto mt-12 max-w-2xl rounded-3xl border border-line bg-white p-6 text-center shadow-card sm:p-8 lg:mt-16">
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-ink-700">
            <Icon name="shield" className="h-5 w-5 shrink-0 text-primary-600" aria-hidden />
            {reassurance}
          </p>
          <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
            <a href={cta.href} className="btn-warm">
              {cta.label}
              <Icon name="arrow-right" className="h-5 w-5" aria-hidden />
            </a>
            <a href={secondaryCta.href} className="btn-secondary">
              {secondaryCta.label}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AfterYouApply;
